import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { customerDevicesTableId } from '@/config/table-ids';
import { ApiError } from '@/lib/api/errors';
import { queryKeys } from '@/lib/api/query-keys';

import {
  confirmParamTemplate,
  listOrderDevices,
  listParamTemplates,
  listProductTypes,
  setParamTemplate,
  verifySerial,
} from './api';
import type { OrderScope, SetParamTemplateBody } from './types';

/**
 * Own key root, deliberately separate from `orderKeys`: this is a device concern an order scopes, not
 * an order sub-resource (ADR-0024). Ticket 06's apply invalidates this **and** the customer devices
 * table's own `tableId` root — the order detail is deliberately not a third root; see
 * `useApplyParamTemplate` below.
 */
export const deviceParamsKeys = {
  all: ['device-params'] as const,
  orderDevices: (orderId: string) => [...deviceParamsKeys.all, 'order-devices', orderId] as const,
  productTypes: () => [...deviceParamsKeys.all, 'product-types'] as const,
  paramTemplates: (productName: string) =>
    [...deviceParamsKeys.all, 'param-templates', productName] as const,
};

/**
 * The eligible Units for the modal's device source. The source is **parameterised by scope** rather
 * than forked per host (ADR-0024); ticket 09 adds the customer branch here, leaving every call site
 * untouched.
 *
 * Enabled unconditionally. Gating the fetch on order status would itself be frontend eligibility
 * logic — the thing AC8 forbids — so the backend is asked every time and answers `[]` when there is
 * nothing to configure.
 */
export function useEligibleDevices(
  scope: OrderScope,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: deviceParamsKeys.orderDevices(scope.orderId),
    queryFn: () => listOrderDevices(scope.orderId),
    enabled: enabled && Boolean(scope.orderId),
  });
}

/**
 * Verifies one serial against Digital Matter. A **mutation, not a query**, deliberately: it must fire
 * only on Step 1's Next (AC9), and a query keyed on the serial would re-issue it on every keystroke.
 * Nothing is invalidated — this is a read-only check that writes no server state.
 */
export function useVerifySerial() {
  return useMutation({ mutationFn: verifySerial });
}

/** The DM Category list. One global list, so no scope in the key. */
export function useProductTypes() {
  return useQuery({ queryKey: deviceParamsKeys.productTypes(), queryFn: listProductTypes });
}

/**
 * The templates for one DM Category. **Keyed on the category**, so changing it refetches rather than
 * filtering client-side (AC4) — and a category the admin has already visited comes back from cache.
 *
 * `staleTime: 0` opts out of the app's 30-second default, and it is **load-bearing for AC6**: the modal
 * is `destroyOnHidden`, so reopening the flow remounts this query — but with a 30s stale window a reopen
 * inside that window would serve the cached list and a template just created in Digital Matter would not
 * appear. The list is tiny and the admin opens this modal deliberately, so the extra request is free.
 */
export function useParamTemplates(productName: string) {
  return useQuery({
    queryKey: deviceParamsKeys.paramTemplates(productName),
    queryFn: () => listParamTemplates(productName),
    enabled: productName !== '',
    staleTime: 0,
  });
}

/**
 * The apply was refused because **this Unit can no longer be configured** — another admin configured it
 * first (`ALREADY_CONFIGURED`), its order moved to an ineligible status, or a replacement was raised
 * against it. The flow ends on these and the eligible list is refreshed so the device leaves the
 * dropdown (AC5).
 *
 * Keyed on the backend's **code**, not on HTTP 409: `param-templates/set` answers 409 for five codes,
 * and two of them — `SERIAL_ALREADY_IN_USE`, `TEST_ASSET_SERIAL_REQUIRED` — are about the *serial* the
 * admin typed, so they must leave the wizard open to be corrected rather than end it (QA 2026-10-08).
 */
const NO_LONGER_CONFIGURABLE = new Set([
  'ALREADY_CONFIGURED',
  'INELIGIBLE_ORDER_STATUS',
  'DEVICE_HAS_REPLACEMENT',
]);
export const isNoLongerConfigurable = (error: unknown): error is ApiError =>
  error instanceof ApiError && NO_LONGER_CONFIGURABLE.has(error.code ?? '');

/**
 * Digital Matter does not know this serial. The deployed `verify-serial` answers this as a **422
 * `SERIAL_NOT_FOUND`**, never as a 200 `{valid: false}` — so it is a verdict that arrives through the
 * error channel, and Step 1 must render it inline rather than as a transport-failure toast (AC).
 */
export const isSerialNotFound = (error: unknown): error is ApiError =>
  error instanceof ApiError && error.code === 'SERIAL_NOT_FOUND';

/** Refreshes the roots the affordance and the device status are read from: **this feature's key
 *  factory** — the eligible-units query behind the ellipsis item, ticket 08's row link and Step 1's
 *  dropdown — and the **Customer Devices `<DataTable>`'s own `tableId` root**. Missing either leaves a
 *  stale affordance (AC9, grilling Q15.3). The order detail is deliberately not a third root: it carries
 *  nothing this flow changes, and importing `orderKeys` would close the `orders ↔ device-params` cycle
 *  ADR-0024 hoisted the Unit codec to `lib` to avoid. */
function useInvalidateDeviceParams(scope: OrderScope) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: deviceParamsKeys.all });
    void qc.invalidateQueries({ queryKey: [customerDevicesTableId(scope.customerId)] });
    // The status also shows on the Device Details page, the Customer Map's device panel and the
    // Dealer's device tabs. Entity roots from `lib`, so no feature import closes a cycle.
    void qc.invalidateQueries({ queryKey: queryKeys.deviceTelemetry.all });
    void qc.invalidateQueries({ queryKey: queryKeys.customerMap.all });
    void qc.invalidateQueries({ queryKey: queryKeys.dealers.all });
  };
}

/**
 * **Call one of two — Set Parameter.** Writes the template and leaves the device in **Pending
 * Confirmation**, returning the `customerDeviceId` the confirmation needs.
 *
 * Deliberately *not* chained to `confirm`. The backend lets `set` overwrite only while the device is
 * Pending Configuration or Pending Confirmation; once confirmed it is Pending QC and a second `set` is a
 * 409 `ALREADY_CONFIGURED`. Confirming here made the BA's Back → choose another template → Set Parameter
 * (comment 20812344 point 4) fail against the real backend, so confirmation moved to Complete
 * (`useConfirmParamTemplate`, QA 2026-10-08).
 *
 * `onSettled`, not `onSuccess`: a no-longer-configurable refusal means the server state changed too, so
 * the eligible list is just as stale as after our own apply. A 500 or a dropped connection changed
 * nothing, and AC4 requires the affordance to stay exactly where it was.
 */
export function useApplyParamTemplate(scope: OrderScope) {
  const invalidate = useInvalidateDeviceParams(scope);
  return useMutation({
    mutationFn: (body: SetParamTemplateBody) => setParamTemplate(body),
    onSettled: (_data, error) => {
      if (error && !isNoLongerConfigurable(error)) return;
      invalidate();
    },
  });
}

/**
 * **Call two of two — Complete.** Moves the device from Pending Confirmation to **Pending QC**.
 *
 * **A failure resolves to `false` rather than throwing**, because it is not a failed apply: the template
 * is on the device and only the last step is missing, and the backend keeps a `pending_confirmation`
 * Unit **eligible** (greenway_api#212) so re-running the wizard finishes the job. It invalidates either
 * way — the device's status changed or will be finished from a fresh list.
 */
export function useConfirmParamTemplate(scope: OrderScope) {
  const invalidate = useInvalidateDeviceParams(scope);
  return useMutation({
    mutationFn: async (customerDeviceId: string) => {
      try {
        await confirmParamTemplate(customerDeviceId);
        return true;
      } catch (error) {
        // Swallowed for the admin, never for us: without this signal nobody would learn that
        // confirmation is failing in production.
        Sentry.captureException(error, {
          tags: { param_template_confirm_failed: customerDeviceId },
        });
        return false;
      }
    },
    onSettled: invalidate,
  });
}
